import React, { useMemo } from 'react';
import { Box, Typography, LinearProgress, Stack, Chip } from '@mui/material';
import { CATEGORICAL_COLORS } from './chartUtils';

// Generic horizontal progress-bar breakdown. `data` items: `{name, count, completed?}`.
// `completed` (optional) renders a secondary "X% <secondaryMetricLabel>" caption.
// `topN` (optional) caps how many items are shown (highest `count` first) —
// leave unset to show everything passed in.
// `zeroCountData` (optional) renders a trailing chip section for known
// categories with no activity yet in the selected range (e.g. "pilot" groups).
const CategoryProgressList = ({ data, unitLabel = 'רשומות', secondaryMetricLabel, topN, zeroCountData, zeroStateLabel = 'אין נתונים עדיין' }) => {
    const sorted = useMemo(() => {
        const ranked = [...(data || [])].sort((a, b) => b.count - a.count);
        return topN ? ranked.slice(0, topN) : ranked;
    }, [data, topN]);
    const maxCount = sorted[0]?.count || 1;
    const total = useMemo(() => sorted.reduce((s, g) => s + g.count, 0), [sorted]);

    if (!sorted.length && !(zeroCountData || []).length) {
        return (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                אין נתונים לטווח שנבחר
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                סה״כ {total.toLocaleString('he-IL')} {unitLabel}
            </Typography>

            <Stack gap={2}>
                {sorted.map((item, i) => {
                    const color = CATEGORICAL_COLORS[i % CATEGORICAL_COLORS.length];
                    const barPct = (item.count / maxCount) * 100;
                    const completedPct = secondaryMetricLabel && item.count > 0
                        ? Math.round(((item.completed || 0) / item.count) * 100)
                        : null;

                    return (
                        <Box key={item.name}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 0.75,
                                }}
                            >
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography variant="body2" fontWeight={600}>
                                        {item.name}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" gap={1.5}>
                                    {completedPct !== null && (
                                        <Typography variant="caption" color="text.secondary">
                                            {completedPct}% {secondaryMetricLabel}
                                        </Typography>
                                    )}
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        sx={{ color, minWidth: 36, textAlign: 'left' }}
                                    >
                                        {item.count.toLocaleString('he-IL')}
                                    </Typography>
                                </Stack>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={barPct}
                                sx={{
                                    height: 9,
                                    borderRadius: 5,
                                    bgcolor: `${color}22`,
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: color,
                                        borderRadius: 5,
                                    },
                                }}
                            />
                        </Box>
                    );
                })}
            </Stack>

            {(zeroCountData || []).length > 0 && (
                <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px dashed rgba(0,0,0,0.15)' }}>
                    <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                        {zeroStateLabel}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {zeroCountData.map((item) => (
                            <Chip
                                key={item.name}
                                size="small"
                                label={item.name}
                                sx={{
                                    bgcolor: 'rgba(0,0,0,0.07)',
                                    border: '1px solid rgba(0,0,0,0.25)',
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    fontSize: 13,
                                    height: 28,
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default CategoryProgressList;
